/**
 * Redux slice (src/store/slices/packagesSlice.js).
 *
 * Data flow (typical):
 * - UI dispatches actions/thunks from `src/store/actions/*`.
 * - `reducers` handles synchronous state updates.
 * - `extraReducers` reacts to thunk pending/fulfilled/rejected actions.
 */

import { createSlice } from "@reduxjs/toolkit";
import {
  CreatePackage,
  Delete_Package,
  SearchPackagebyPackageName,
  Search_Free_Package,
  fetchAllpackages,
  fetchPackage,
  updatePackage,
  fetchStudentPackages,
  deductFreeHoursFromPackage,
  addFreeHour,
  getStudentFreeHourUsage,
  fetchPackagesWithCourses, // Import the new action
} from "../actions/packagesActions";
import { toast } from "react-toastify";
import { t } from "../../utils/i18nTranslate";

let initialState = {
  packageslist: {
    packages: []
  },
  packagesWithCourses: [], // Add new state for packages with courses
  currentPackage: null,
  Teacher_Packages: [],
  FreePackage: null,
  studentPackages: [],
  studentFreeHourUsage: [],
  loading: false,
  error: null
};

/**
 * RTK slice definition: packageSlice.
 */
const packageSlice = createSlice({
  name: "package",
  initialState,
  reducers: {},
  /**
   * Reducer: extraReducers.
   */
  extraReducers: (builder) => {
    builder
      // Add cases for the new fetchPackagesWithCourses action
      .addCase(fetchPackagesWithCourses.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      // extraReducers handler: fetchPackagesWithCourses.fulfilled
      .addCase(fetchPackagesWithCourses.fulfilled, (state, action) => {
        state.packagesWithCourses = action.payload.packages;
        state.loading = false;
        state.error = null;
      })
      // extraReducers handler: fetchPackagesWithCourses.rejected
      .addCase(fetchPackagesWithCourses.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
        toast.error(t("store.packages.fetchWithCoursesFailed"));
      })
      
      // Existing cases
      .addCase(Delete_Package.pending, (state) => {
        state.loading = true;
      })
      // extraReducers handler: Delete_Package.fulfilled
      .addCase(Delete_Package.fulfilled, (state, action) => {
        state.packageslist.packages = state.packageslist.packages.filter(
          (pkg) => pkg._id !== action.payload.DeletedPackage._id
        );
        state.loading = false;
        toast.success(t("store.packages.deletedSuccessfully"));
      })
      // extraReducers handler: Delete_Package.rejected
      .addCase(Delete_Package.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
        toast.error(t("store.packages.deleteFailed"));
      })
      // extraReducers handler: fetchAllpackages.pending
      .addCase(fetchAllpackages.pending, (state) => {
        state.loading = true;
      })
      // extraReducers handler: fetchAllpackages.fulfilled
      .addCase(fetchAllpackages.fulfilled, (state, action) => {
        state.packageslist = action.payload;
        state.loading = false;
      })
      // extraReducers handler: fetchAllpackages.rejected
      .addCase(fetchAllpackages.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })
      // extraReducers handler: CreatePackage.pending
      .addCase(CreatePackage.pending, (state) => {
        state.loading = true;
      })
      // extraReducers handler: CreatePackage.fulfilled
      .addCase(CreatePackage.fulfilled, (state, action) => {
        state.packageslist.packages.push(action.payload.newPackage);
        state.loading = false;
        toast.success(t("store.packages.createdSuccessfully"));
      })
      // extraReducers handler: CreatePackage.rejected
      .addCase(CreatePackage.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
        toast.error(t("store.packages.createFailed"));
      })
      // extraReducers handler: updatePackage.pending
      .addCase(updatePackage.pending, (state) => {
        state.loading = true;
      })
      // extraReducers handler: updatePackage.fulfilled
      .addCase(updatePackage.fulfilled, (state, action) => {
        const index = state.packageslist.packages.findIndex(
          pkg => pkg._id === action.payload.updatedPackage._id
        );
        if (index !== -1) {
          state.packageslist.packages[index] = action.payload.updatedPackage;
        }
        state.loading = false;
        toast.success(t("store.packages.updatedSuccessfully"));
      })
      // extraReducers handler: updatePackage.rejected
      .addCase(updatePackage.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
        toast.error(t("store.packages.updateFailed"));
      })
      // extraReducers handler: fetchPackage.pending
      .addCase(fetchPackage.pending, (state) => {
        state.loading = true;
      })
      // extraReducers handler: fetchPackage.fulfilled
      .addCase(fetchPackage.fulfilled, (state, action) => {
        state.currentPackage = action.payload;
        state.loading = false;
      })
      // extraReducers handler: fetchPackage.rejected
      .addCase(fetchPackage.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })
      // extraReducers handler: Search_Free_Package.pending
      .addCase(Search_Free_Package.pending, (state) => {
        state.loading = true;
      })
      // extraReducers handler: Search_Free_Package.fulfilled
      .addCase(Search_Free_Package.fulfilled, (state, action) => {
        state.FreePackage = action.payload.FreePackage;
        state.loading = false;
      })
      // extraReducers handler: Search_Free_Package.rejected
      .addCase(Search_Free_Package.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
        toast.error(t("store.packages.fetchFreePackageFailed"));
      })
      // extraReducers handler: SearchPackagebyPackageName.pending
      .addCase(SearchPackagebyPackageName.pending, (state) => {
        state.loading = true;
      })
      // extraReducers handler: SearchPackagebyPackageName.fulfilled
      .addCase(SearchPackagebyPackageName.fulfilled, (state, action) => {
        state.packageslist = action.payload.foundedPackage || [];
        state.loading = false;
      })
      // extraReducers handler: SearchPackagebyPackageName.rejected
      .addCase(SearchPackagebyPackageName.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
        toast.error(t("store.packages.searchFailed"));
      })
      // extraReducers handler: fetchStudentPackages.pending
      .addCase(fetchStudentPackages.pending, (state) => {
        state.loading = true;
      })
      // extraReducers handler: fetchStudentPackages.fulfilled
      .addCase(fetchStudentPackages.fulfilled, (state, action) => {
        state.studentPackages = action.payload;
        state.loading = false;
      })
      // extraReducers handler: fetchStudentPackages.rejected
      .addCase(fetchStudentPackages.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
        toast.error(t("store.packages.fetchStudentPackagesFailed"));
      })
      // extraReducers handler: deductFreeHoursFromPackage.fulfilled
      .addCase(deductFreeHoursFromPackage.fulfilled, (state, action) => {
        const updatedPackage = action.payload;
        const packagesArray = Array.isArray(state.packageslist?.packages)
          ? state.packageslist.packages
          : [];
        const index = packagesArray.findIndex(pkg => pkg._id === updatedPackage._id);
        if (index !== -1) {
          packagesArray[index] = updatedPackage;
        }
        if (state.currentPackage && state.currentPackage._id === updatedPackage._id) {
          state.currentPackage = updatedPackage;
        }
      })
      // extraReducers handler: addFreeHour.pending
      .addCase(addFreeHour.pending, (state) => {
        state.loading = true;
      })
      // extraReducers handler: addFreeHour.fulfilled
      .addCase(addFreeHour.fulfilled, (state, action) => {
        state.loading = false;
        // Update the relevant package in the state
        const updatedPackage = action.payload.freeHours;
        const packagesArray = Array.isArray(state.packageslist?.packages)
          ? state.packageslist.packages
          : [];
        const index = packagesArray.findIndex(pkg => pkg._id === updatedPackage.packageId);
        if (index !== -1) {
          packagesArray[index] = {
            ...packagesArray[index],
            freeHours: updatedPackage
          };
        }
        // Update current package if it's the one being modified
        if (state.currentPackage && state.currentPackage._id === updatedPackage.packageId) {
          state.currentPackage = {
            ...state.currentPackage,
            freeHours: updatedPackage
          };
        }
        // Update student packages if necessary
        const studentPackageIndex = state.studentPackages.findIndex(pkg => pkg._id === updatedPackage.packageId);
        if (studentPackageIndex !== -1) {
          state.studentPackages[studentPackageIndex] = {
            ...state.studentPackages[studentPackageIndex],
            freeHours: updatedPackage
          };
        }
        toast.success(action.payload.Free_hour_used ? t("store.packages.freeHourUsedSuccessfully") : t("store.packages.freeHourReturnedSuccessfully"));
      })
      // extraReducers handler: addFreeHour.rejected
      .addCase(addFreeHour.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
        toast.error(t("store.packages.freeHourFailed"));
      })
      // extraReducers handler: getStudentFreeHourUsage.pending
      .addCase(getStudentFreeHourUsage.pending, (state) => {
        state.loading = true;
      })
      // extraReducers handler: getStudentFreeHourUsage.fulfilled
      .addCase(getStudentFreeHourUsage.fulfilled, (state, action) => {
        state.loading = false;
        state.studentFreeHourUsage = action.payload.data;
      })
      // extraReducers handler: getStudentFreeHourUsage.rejected
      .addCase(getStudentFreeHourUsage.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
        toast.error(t("store.packages.fetchFreeHourUsageFailed"));
      });
  },
});

export default packageSlice.reducer;
